import React, { useEffect } from "react";

import isIntuit from "Intuit/isIntuit";
import { useDispatch } from "react-redux";
import AnalyticsTracking from "Utils/AnalyticsTracking";

import { SubBody } from "Cobalt/BodyText";
import { Collapse } from "Cobalt/Collapse";
import { openModal } from "Cobalt/Modal";
import Toggle from "Cobalt/Toggle";
import Button from "Common/Button";
import { Row, Col } from "Common/Grid";
import { Hr, Div } from "Common/HTML";
import Icon from "Common/Icon";
import { Body2 as BodyText } from "Theme/BodyText";

import { ALTERNATE_DOCS_MODAL_NAME } from "./constants";
import { getModalName, docTypeForAnalytics, useDocumentPickerContext, bem, responsiveBem } from "./utils";

const ChangeButton = ({ openAltDocsModal }) => (
  <Button pt={0} color="link" onClick={openAltDocsModal}>
    Change
  </Button>
);

const SuccessMessage = () => (
  <Row mt={2}>
    <Col>
      <BodyText>
        <p className={responsiveBem("Success")}>
          <Icon icon="check" pr="sm" style={{ marginRight: "2px" }} />
          Upload Successful
        </p>
      </BodyText>
    </Col>
  </Row>
);

const ExplanationCardContent = ({ openAltDocsModal, persistedExplanation, uploadDocsComplete }) => (
  <Div className={bem("ExplanationBox")}>
    <Col md={9} pl={0}>
      <BodyText weight="bold">No documents were provided</BodyText>
      {uploadDocsComplete && <SuccessMessage />}
      <BodyText mt={3} muted className={bem("ExplanationText")}>
        {persistedExplanation}
      </BodyText>
    </Col>
    <Col md={3} p={0}>
      <ChangeButton openAltDocsModal={openAltDocsModal} />
    </Col>
  </Div>
);

const AltDocsContent = ({ titles, filenames, openAltDocsModal }) =>
  filenames.map((filename, index) => {
    const title = titles[index];
    if (title) {
      return (
        <div key={index}>
          <BodyText weight="bold" mt={4} mb={3}>
            {title}
          </BodyText>
          {filename ? (
            <>
              <SubBody className={bem("AltSubBody")}>{filename}</SubBody>
              <ChangeButton openAltDocsModal={openAltDocsModal} />
            </>
          ) : (
            <Button color="gray" onClick={openAltDocsModal}>
              Upload
            </Button>
          )}
        </div>
      );
    }
  });

const InitialCardContent = ({ openAltDocsModal }) => (
  <>
    <BodyText mb={4}>You can upload other documents that you may have</BodyText>
    <Button color="gray" size={isIntuit() ? "medium" : "small"} onClick={openAltDocsModal} fullWidth>
      Show alternative documents to upload
    </Button>
  </>
);

const onToggle = (setAltDocsCardIsOpen, altDocsCardIsOpen, fileInputName) => {
  setAltDocsCardIsOpen(!altDocsCardIsOpen);
  const docTypeString = docTypeForAnalytics(fileInputName);
  AnalyticsTracking.trackEvent(`${docTypeString}_toggle_${altDocsCardIsOpen ? "off" : "on"}`, {
    location: "document_picker",
    main_file_type: fileInputName,
  });
};

const AlternateDocumentsCardSection = () => {
  const {
    index,
    fileInputName,
    primaryAlternatives,
    secondaryAlternatives,
    pickerState: { uploadDocsComplete, processing },
    userFlagsState: { persistedExplanation, persistedAlternativeType },
    alternativeDocumentsState: { altDocsCardIsOpen, setAltDocsCardIsOpen, altFilenames, fullfilled },
  } = useDocumentPickerContext();
  const dispatch = useDispatch();

  useEffect(() => {
    const docTypeString = docTypeForAnalytics(fileInputName);
    if (fullfilled) {
      AnalyticsTracking.trackEvent(`all_alternative_${docTypeString}s_uploaded`, {
        location: "document_picker",
        main_file_type: fileInputName,
      });
    }
  }, [fullfilled]);

  const openAltDocsModal = () => dispatch(openModal(getModalName(ALTERNATE_DOCS_MODAL_NAME, index)));

  const cardContent = () => {
    if (persistedAlternativeType === "NoDocuments") {
      return <ExplanationCardContent openAltDocsModal={openAltDocsModal} persistedExplanation={persistedExplanation} uploadDocsComplete={uploadDocsComplete} />;
    }
    if (persistedAlternativeType === "primary" || persistedAlternativeType === "secondary") {
      const filenames = altFilenames[persistedAlternativeType];
      const alternativeDocs = persistedAlternativeType === "primary" ? primaryAlternatives : secondaryAlternatives;

      return (
        <>
          <BodyText>You can upload other documents that you may have</BodyText>
          {uploadDocsComplete && <SuccessMessage />}
          <AltDocsContent titles={alternativeDocs.map((doc) => doc.title)} filenames={Object.values(filenames)} openAltDocsModal={openAltDocsModal} />
        </>
      );
    }
    return <InitialCardContent openAltDocsModal={openAltDocsModal} />;
  };

  return processing ? null : (
    <>
      <Row py={3}>
        <Col>
          <Toggle
            onToggle={() => onToggle(setAltDocsCardIsOpen, altDocsCardIsOpen, fileInputName)}
            labelText="I do not have this file"
            // Setting the toggleState manually based in the open state of the card is
            // a bit circular but is needed for setting the initial state of the toggle.
            // Otherwise this card could end up being open but the toggle set to "off" position.
            toggleState={altDocsCardIsOpen}
            id={`toggle_${index}`}
          />
        </Col>
      </Row>
      <Collapse in={altDocsCardIsOpen}>
        <div>
          <Row>
            <Hr />
            <Col md={9}>{cardContent()}</Col>
          </Row>
        </div>
      </Collapse>
    </>
  );
};

export default AlternateDocumentsCardSection;
